/**
 * Growing Numbers Animation
 *
 * Anime les éléments avec la classe .has-growing-number
 * en faisant croître progressivement leur valeur numérique
 * lorsqu'ils deviennent visibles dans le viewport.
 */

document.addEventListener('DOMContentLoaded', () => {

	// Ne pas animer dans l'éditeur Gutenberg
	if (document.body.classList.contains('block-editor-page')) {
		return;
	}

	const numberElements = document.querySelectorAll('.has-growing-number');

	// Formate les nombres avec séparateurs de milliers
	const formatNumber = (number) => {
		return new Intl.NumberFormat('fr-FR').format(number);
	};

	// Extrait le nombre final + signe + suffixe
	const parseNumberElement = (element) => {
		const text = element.textContent.trim();

		// Détection du signe éventuel (ex : "+  " ou "- ")
		const signMatch = text.match(/^([+-]\s*)/);
		const sign = signMatch ? signMatch[0] : '';

		// Retire le signe pour parser le reste
		const textWithoutSign = sign ? text.substring(sign.length) : text;
		const cleanedText = textWithoutSign.replace(/\s+/g, '');

		// Détecte chiffre(s) et suffixe
		const matches = cleanedText.match(/^(\d+)(.*)$/);
		if (matches) {
			const digits = matches[1];
			const suffix = matches[2] || '';
			const numberValue = parseInt(digits, 10);
			const number = sign.includes('-') ? -numberValue : numberValue;

			return { number, sign, suffix, originalText: text };
		}

		console.warn('Impossible de parser le nombre:', text);
		return null;
	};

	// Anime la valeur d'un span.number de 0 jusqu'à end
	const animateValue = (element, end, sign = '', suffix = '') => {
		const numberElement = element.querySelector('.number');
		if (!numberElement) return;

		const duration = 1500; // 1.5 secondes
		const startTime = performance.now();
		const range = Math.abs(end);

		let didPulse = false;
		const easeOutQuad = t => t * (2 - t);

		// Calcule un "step" pour éviter trop de rafraîchissements
		const getStep = (valueRange) => {
			if (valueRange > 1000) return 10;
			if (valueRange > 100) return 5;
			if (valueRange > 10) return 1;
			return 0.1;
		};
		const step = getStep(range);
		let lastDisplayedValue = null;

		const updateNumber = (currentTime) => {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);
			const easedProgress = easeOutQuad(progress);

			let current = Math.round(range * easedProgress);
			current = Math.round(current / step) * step;

			// Met à jour uniquement si ça a changé
			if (current !== lastDisplayedValue) {
				lastDisplayedValue = current;
				// Applique le signe si besoin
				numberElement.textContent = sign + formatNumber(current);

				// Pulsation finale
				if (progress > 0.9 && !didPulse) {
					didPulse = true;
					numberElement.classList.add('is-growing');
					setTimeout(() => {
						numberElement.classList.remove('is-growing');
					}, 600);
				}
			}

			if (progress < 1) {
				requestAnimationFrame(updateNumber);
			} else {
				// Valeur finale exacte
				numberElement.textContent = sign + formatNumber(end);
			}
		};

		requestAnimationFrame(updateNumber);
	};

	//-------------------------------------------
	// 1. Pré-initialisation : stocker la valeur,
	//    remplacer par "0" et masquer l'élément
	//-------------------------------------------
	numberElements.forEach(element => {
		const parsed = parseNumberElement(element);
		if (parsed) {
			// Stocke les infos dans des data-attributes
			element.dataset.finalNumber = parsed.number;
			element.dataset.sign = parsed.sign;
			element.dataset.suffix = parsed.suffix;

			// Remplace le texte par un span.number à 0
			element.innerHTML = `<span class="number" data-suffix="${parsed.suffix}">0</span>`;

			// On ajoute une classe ou un style pour masquer l'élément
			element.style.visibility = 'hidden';
			// On peut aussi ajouter element.classList.add('is-hidden'); et gérer en SCSS.
		}
	});

	//-------------------------------------------
	// 2. IntersectionObserver : quand l'élément
	//    est visible, on l'affiche et on anime
	//-------------------------------------------
	const observer = new IntersectionObserver((entries, obs) => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				const element = entry.target;

				// On affiche l'élément
				element.style.visibility = 'visible';
				element.classList.add('is-visible');

				// Récupère les infos stockées
				const end = parseInt(element.dataset.finalNumber, 10) || 0;
				const sign = element.dataset.sign || '';
				const suffix = element.dataset.suffix || '';

				// Anime
				animateValue(element, end, sign, suffix);

				// Stop l'observation
				obs.unobserve(element);
			}
		});
	}, {
		root: null, // le viewport
		rootMargin: '0px 0px 0px 0px', // retarder un peu
		threshold: 0
	});

	// 3. Lancement de l'observation
	numberElements.forEach(element => {
		observer.observe(element);
	});

	//-------------------------------------------
	// 4. Fonction manuelle (optionnelle)
	//-------------------------------------------
	window.initGrowingNumber = (selector) => {
		const elements = selector
			? document.querySelectorAll(selector)
			: document.querySelectorAll('.has-growing-number');

		elements.forEach(element => {
			// Déjà animé ?
			if (element.style.visibility !== 'hidden') return;

			element.style.visibility = 'visible';
			element.classList.add('is-visible');

			const end = parseInt(element.dataset.finalNumber, 10) || 0;
			const sign = element.dataset.sign || '';
			const suffix = element.dataset.suffix || '';

			animateValue(element, end, sign, suffix);
		});
	};
});
